import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { profilesApi, sectionsApi, extolsApi, invitesApi, contributionsApi, galleryApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { 
  Heart, ArrowLeft, Save, Eye, Loader2, Upload, Trash2, Plus, Copy, Check, 
  BookOpen, Sparkles, Clock, Star, MessageCircle, Users, Link as LinkIcon,
  GraduationCap, Palette, Shield, Globe, Briefcase, Image, MapPin, X
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const SECTION_TYPES = [
  { id: 'story', name: 'Their Story', icon: BookOpen, prompt: 'Share their journey - where they came from, what shaped them.' },
  { id: 'impact', name: 'Their Impact', icon: Sparkles, prompt: 'How did they change your life? What did they give you?' },
  { id: 'moments', name: 'Cherished Moments', icon: Clock, prompt: 'The memories that stay with you.' },
  { id: 'values', name: 'What They Stand For', icon: Star, prompt: 'The principles they lived by.' },
  { id: 'messages', name: 'Words of Love', icon: MessageCircle, prompt: 'Direct words of gratitude.' }
];

const CONTRIBUTION_CATEGORIES = [
  { id: 'academic', name: 'Academic', icon: GraduationCap, desc: 'Teaching, research, mentoring, scholarships' },
  { id: 'arts', name: 'Arts & Culture', icon: Palette, desc: 'Music, writing, visual arts, performance' },
  { id: 'military', name: 'Military & Service', icon: Shield, desc: 'War service, emergency services, first responders' },
  { id: 'community', name: 'Community', icon: Users, desc: 'Local volunteering, neighbourhood impact' },
  { id: 'global', name: 'Global', icon: Globe, desc: 'International work, humanitarian efforts' },
  { id: 'professional', name: 'Professional', icon: Briefcase, desc: 'Career achievements, industry impact' }
];

export default function EditTributePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [sections, setSections] = useState([]);
  const [extols, setExtols] = useState([]);
  const [invites, setInvites] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showStockPhotos, setShowStockPhotos] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');

  const STOCK_PHOTOS = [
    { url: 'https://images.pexels.com/photos/33037623/pexels-photo-33037623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Lavender fields' },
    { url: 'https://images.pexels.com/photos/2839837/pexels-photo-2839837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Green hills & lake' },
    { url: 'https://images.pexels.com/photos/6428726/pexels-photo-6428726.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Peach roses' },
    { url: 'https://images.pexels.com/photos/7462760/pexels-photo-7462760.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Rose bouquet' },
    { url: 'https://images.pexels.com/photos/32029058/pexels-photo-32029058.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Zinnia field' },
    { url: 'https://images.pexels.com/photos/36959506/pexels-photo-36959506.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', label: 'Chrysanthemums' },
    { url: 'https://images.unsplash.com/photo-1683027359658-ad9d823cd0e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjaGVycnklMjBibG9zc29tJTIwdHJlZSUyMHNwcmluZ3xlbnwwfHx8fDE3NzY3NjUxNzd8MA&ixlib=rb-4.1.0&q=85', label: 'Cherry blossom' },
    { url: 'https://images.unsplash.com/photo-1683027359098-11dd6ad95554?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjaGVycnklMjBibG9zc29tJTIwdHJlZSUyMHNwcmluZ3xlbnwwfHx8fDE3NzY3NjUxNzd8MA&ixlib=rb-4.1.0&q=85', label: 'Blossoms' },
  ];

  const [editedProfile, setEditedProfile] = useState({
    person_name: '', relationship: '', headline: '',
    profile_type: 'living', privacy_setting: 'private',
    location_city: '', location_country: ''
  });

  const [newSection, setNewSection] = useState({ section_type: '', content: '' });
  const [addingSection, setAddingSection] = useState(false);
  const [newExtol, setNewExtol] = useState('');
  const [addingExtol, setAddingExtol] = useState(false);
  const [newContrib, setNewContrib] = useState({ category: '', title: '', description: '', year: '' });
  const [addingContrib, setAddingContrib] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [profileRes, sectionsRes, extolsRes, invitesRes, contribRes, galleryRes] = await Promise.all([
        profilesApi.getById(id),
        sectionsApi.getAll(id),
        extolsApi.getAll(id),
        invitesApi.getAll(id).catch(() => ({ data: [] })),
        contributionsApi.getAll(id).catch(() => ({ data: [] })),
        galleryApi.getAll(id).catch(() => ({ data: [] }))
      ]);
      setProfile(profileRes.data);
      setEditedProfile({
        person_name: profileRes.data.person_name,
        relationship: profileRes.data.relationship,
        headline: profileRes.data.headline,
        profile_type: profileRes.data.profile_type,
        privacy_setting: profileRes.data.privacy_setting,
        location_city: profileRes.data.location_city || '',
        location_country: profileRes.data.location_country || ''
      });
      setSections(sectionsRes.data);
      setExtols(extolsRes.data);
      setInvites(invitesRes.data);
      setContributions(contribRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      toast.error('Failed to load tribute');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await profilesApi.update(id, editedProfile);
      setProfile({ ...profile, ...editedProfile });
      toast.success('Tribute saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const { data } = await profilesApi.uploadPhoto(id, file);
      setProfile({ ...profile, photo_url: data.photo_url });
      toast.success('Photo uploaded');
    } catch { toast.error('Failed to upload photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSelectStockPhoto = async (stockUrl) => {
    setUploadingPhoto(true);
    try {
      const { data } = await profilesApi.setStockPhoto(id, stockUrl);
      setProfile({ ...profile, photo_url: data.photo_url });
      setShowStockPhotos(false);
      toast.success('Photo set');
    } catch { toast.error('Failed to set photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingGallery(true);
    try {
      for (const file of files) {
        const { data } = await galleryApi.upload(id, file);
        setGallery(prev => [...prev, data]);
      }
      toast.success(`${files.length} image(s) added to gallery`);
    } catch { toast.error('Failed to upload image'); }
    finally { setUploadingGallery(false); }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    try {
      await galleryApi.delete(id, imageId);
      setGallery(gallery.filter(g => g.id !== imageId));
      toast.success('Image removed');
    } catch { toast.error('Failed to remove image'); }
  };

  const handleAddSection = async () => {
    if (!newSection.section_type || !newSection.content.trim()) return;
    setAddingSection(true);
    try {
      const { data } = await sectionsApi.create(id, { ...newSection, order: sections.length });
      setSections([...sections, data]);
      setNewSection({ section_type: '', content: '' });
      toast.success('Section added');
    } catch { toast.error('Failed to add section'); }
    finally { setAddingSection(false); }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await sectionsApi.delete(id, sectionId);
      setSections(sections.filter(s => s.id !== sectionId));
      toast.success('Section deleted');
    } catch { toast.error('Failed to delete section'); }
  };

  const handleAddContribution = async () => {
    if (!newContrib.category || !newContrib.title.trim()) return;
    setAddingContrib(true);
    try {
      const { data } = await contributionsApi.create(id, newContrib);
      setContributions([data, ...contributions]);
      setNewContrib({ category: '', title: '', description: '', year: '' });
      toast.success('Contribution added');
    } catch { toast.error('Failed to add contribution'); }
    finally { setAddingContrib(false); }
  };

  const handleDeleteContribution = async (contribId) => {
    try {
      await contributionsApi.delete(id, contribId);
      setContributions(contributions.filter(c => c.id !== contribId));
      toast.success('Contribution deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleAddExtol = async () => {
    if (!newExtol.trim()) return;
    setAddingExtol(true);
    try {
      const { data } = await extolsApi.create(id, { message: newExtol });
      setExtols([data, ...extols]);
      setNewExtol('');
      toast.success('Extol added');
    } catch { toast.error('Failed to add extol'); }
    finally { setAddingExtol(false); }
  };

  const handleDeleteExtol = async (extolId) => {
    try {
      await extolsApi.delete(id, extolId);
      setExtols(extols.filter(e => e.id !== extolId));
      toast.success('Extol deleted');
    } catch { toast.error('Failed to delete extol'); }
  };

  const handleCreateInvite = async () => {
    try {
      const { data } = await invitesApi.create(id, {});
      setInvites([...invites, data]);
      toast.success('Invite link created');
    } catch { toast.error('Failed to create invite'); }
  };

  const copyInviteLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/tribute/${id}?invite=${token}`);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
    toast.success('Link copied');
  };

  const getSectionIcon = (type) => (SECTION_TYPES.find(s => s.id === type)?.icon || BookOpen);
  const getSectionPrompt = (type) => (SECTION_TYPES.find(s => s.id === type)?.prompt || '');

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#7c3aed] animate-spin" /></div>;
  if (!profile) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><p className="text-[#1a1a2e]/50">Tribute not found</p></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-100/50 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-pink-100/50 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e]" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Dashboard</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to={`/tribute/${id}`}>
              <Button variant="outline" size="sm" className="border-[#1a1a2e]/10 text-[#1a1a2e]/70 rounded-full" data-testid="preview-button">
                <Eye className="w-4 h-4 mr-2" />Preview
              </Button>
            </Link>
            <Button size="sm" onClick={handleSaveProfile} disabled={saving}
              className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full shadow-lg shadow-purple-500/20"
              data-testid="save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Profile Header */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="md:col-span-1">
            <div className="aspect-square rounded-3xl bg-gradient-to-br from-purple-50 to-pink-50 border border-[#1a1a2e]/5 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#7c3aed]/30 transition-all relative group shadow-lg"
              onClick={() => fileInputRef.current?.click()} data-testid="photo-upload-area">
              {profile.photo_url ? (
                <>
                  <img src={profile.photo_url.startsWith('http') ? profile.photo_url : `/api${profile.photo_url.startsWith('/api') ? profile.photo_url.slice(4) : profile.photo_url}`} alt={profile.person_name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#1a1a2e]/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Upload className="w-8 h-8 text-white" />
                  </div>
                </>
              ) : (
                <div className="text-center p-6">
                  {uploadingPhoto ? <Loader2 className="w-10 h-10 text-[#7c3aed] animate-spin mx-auto" /> : (
                    <><Upload className="w-10 h-10 text-[#1a1a2e]/20 mx-auto mb-3" /><p className="text-sm text-[#1a1a2e]/40">Click to upload photo</p></>
                  )}
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            {!profile.photo_url && (
              <Button variant="outline" size="sm" className="w-full mt-2 rounded-xl border-[#1a1a2e]/10 text-[#1a1a2e]/60 text-xs"
                onClick={(e) => { e.stopPropagation(); setShowStockPhotos(true); }} data-testid="stock-photo-btn">
                <Image className="w-3 h-3 mr-1.5" />Choose from gallery
              </Button>
            )}
          </div>

          <div className="md:col-span-2 space-y-4">
            <Badge className={`${profile.profile_type === 'living' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-violet-50 text-violet-600 border-violet-200'} border rounded-full`}>
              {profile.profile_type === 'living' ? <Sparkles className="w-3 h-3 mr-1" /> : <Star className="w-3 h-3 mr-1" />}
              {profile.profile_type === 'living' ? 'Living Tribute' : 'Legacy Tribute'}
            </Badge>
            
            <Input value={editedProfile.person_name} onChange={(e) => setEditedProfile({ ...editedProfile, person_name: e.target.value })}
              className="text-3xl font-bold bg-transparent border-0 border-b border-[#1a1a2e]/10 rounded-none px-0 py-2 focus:ring-0 focus:border-[#7c3aed]/50 text-[#1a1a2e]"
              placeholder="Their name" data-testid="edit-person-name" />
            
            <Input value={editedProfile.relationship} onChange={(e) => setEditedProfile({ ...editedProfile, relationship: e.target.value })}
              className="text-lg bg-transparent border-0 border-b border-[#1a1a2e]/10 rounded-none px-0 py-2 focus:ring-0 focus:border-[#7c3aed]/50 text-[#1a1a2e]/70"
              placeholder="Relationship" data-testid="edit-relationship" />

            <div className="grid grid-cols-2 gap-3">
              <Input value={editedProfile.location_city} onChange={(e) => setEditedProfile({ ...editedProfile, location_city: e.target.value })}
                className="bg-white border-[#1a1a2e]/10 rounded-xl shadow-sm text-sm" placeholder="City / Town" data-testid="edit-location-city" />
              <Input value={editedProfile.location_country} onChange={(e) => setEditedProfile({ ...editedProfile, location_country: e.target.value })}
                className="bg-white border-[#1a1a2e]/10 rounded-xl shadow-sm text-sm" placeholder="Country" data-testid="edit-location-country" />
            </div>
            
            <Textarea value={editedProfile.headline} onChange={(e) => setEditedProfile({ ...editedProfile, headline: e.target.value })}
              className="bg-white border-[#1a1a2e]/10 focus:border-[#7c3aed]/50 text-[#1a1a2e] rounded-xl resize-none shadow-sm"
              placeholder="How did they impact your life?" rows={3} data-testid="edit-headline" />

            <div className="flex gap-3 flex-wrap">
              <Select value={editedProfile.profile_type} onValueChange={(v) => setEditedProfile({ ...editedProfile, profile_type: v })}>
                <SelectTrigger className="w-36 bg-white border-[#1a1a2e]/10 rounded-xl shadow-sm" data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="living">Living</SelectItem><SelectItem value="legacy">Legacy</SelectItem></SelectContent>
              </Select>
              <Select value={editedProfile.privacy_setting} onValueChange={(v) => setEditedProfile({ ...editedProfile, privacy_setting: v })}>
                <SelectTrigger className="w-36 bg-white border-[#1a1a2e]/10 rounded-xl shadow-sm" data-testid="edit-privacy-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="private">Private</SelectItem><SelectItem value="invite-only">Invite Only</SelectItem><SelectItem value="public">Public</SelectItem></SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setShowInviteDialog(true)} className="border-[#1a1a2e]/10 rounded-xl" data-testid="invite-button">
                <Users className="w-4 h-4 mr-2" />Invite
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-2xl grid-cols-4 bg-[#1a1a2e]/5 rounded-full p-1 mb-8">
            <TabsTrigger value="sections" className="rounded-full text-[#1a1a2e]/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="sections-tab">Story</TabsTrigger>
            <TabsTrigger value="contributions" className="rounded-full text-[#1a1a2e]/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="contributions-tab">Contributions</TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-full text-[#1a1a2e]/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="gallery-tab">Gallery ({gallery.length})</TabsTrigger>
            <TabsTrigger value="extols" className="rounded-full text-[#1a1a2e]/50 data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="extols-tab">Extols ({extols.length})</TabsTrigger>
          </TabsList>

          {/* Sections Tab */}
          <TabsContent value="sections" className="space-y-6">
            {sections.map((section) => {
              const Icon = getSectionIcon(section.section_type);
              return (
                <Card key={section.id} className="border-[#1a1a2e]/5 bg-white shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#7c3aed]" />
                        </div>
                        <CardTitle className="text-lg font-semibold text-[#1a1a2e]">
                          {SECTION_TYPES.find(s => s.id === section.section_type)?.name || section.section_type}
                        </CardTitle>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteSection(section.id)}
                        className="text-[#1a1a2e]/30 hover:text-red-500 hover:bg-red-50" data-testid={`delete-section-${section.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Textarea value={section.content}
                      onChange={(e) => setSections(sections.map(s => s.id === section.id ? { ...s, content: e.target.value } : s))}
                      onBlur={(e) => sectionsApi.update(id, section.id, { content: e.target.value })}
                      className="bg-[#FDFBF7] border-[#1a1a2e]/5 focus:border-[#7c3aed]/50 text-[#1a1a2e] rounded-xl min-h-[120px]"
                      placeholder={getSectionPrompt(section.section_type)} data-testid={`section-content-${section.id}`} />
                  </CardContent>
                </Card>
              );
            })}
            <Card className="border-[#1a1a2e]/10 border-dashed bg-transparent">
              <CardContent className="p-6 space-y-4">
                <p className="font-semibold text-[#1a1a2e] flex items-center gap-2"><Plus className="w-5 h-5" />Add Story Section</p>
                <Select value={newSection.section_type} onValueChange={(v) => setNewSection({ ...newSection, section_type: v })}>
                  <SelectTrigger className="bg-white border-[#1a1a2e]/10 rounded-xl" data-testid="new-section-type"><SelectValue placeholder="Choose section type" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {SECTION_TYPES.map(t => (<SelectItem key={t.id} value={t.id}><span className="flex items-center gap-2"><t.icon className="w-4 h-4" />{t.name}</span></SelectItem>))}
                  </SelectContent>
                </Select>
                {newSection.section_type && (
                  <>
                    <p className="text-sm text-[#1a1a2e]/40 italic p-3 bg-purple-50/50 rounded-xl">{getSectionPrompt(newSection.section_type)}</p>
                    <Textarea value={newSection.content} onChange={(e) => setNewSection({ ...newSection, content: e.target.value })}
                      className="bg-white border-[#1a1a2e]/10 rounded-xl min-h-[120px] shadow-sm" placeholder="Write here..." data-testid="new-section-content" />
                    <Button onClick={handleAddSection} disabled={addingSection || !newSection.content.trim()}
                      className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full shadow-lg" data-testid="add-section-button">
                      {addingSection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Add Section
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contributions Tab */}
          <TabsContent value="contributions" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {contributions.map(c => {
                const cat = CONTRIBUTION_CATEGORIES.find(cc => cc.id === c.category);
                const Icon = cat?.icon || Star;
                return (
                  <Card key={c.id} className="border-[#1a1a2e]/5 bg-white shadow-sm hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5 text-[#7c3aed]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#1a1a2e]">{c.title}</p>
                            <Badge variant="outline" className="text-xs mt-1">{cat?.name || c.category}</Badge>
                            {c.year && <span className="text-xs text-[#1a1a2e]/40 ml-2">{c.year}</span>}
                            {c.description && <p className="text-sm text-[#1a1a2e]/60 mt-2">{c.description}</p>}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteContribution(c.id)}
                          className="text-[#1a1a2e]/30 hover:text-red-500 hover:bg-red-50 shrink-0" data-testid={`delete-contrib-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="border-[#1a1a2e]/10 border-dashed bg-transparent">
              <CardContent className="p-6 space-y-4">
                <p className="font-semibold text-[#1a1a2e] flex items-center gap-2"><Plus className="w-5 h-5" />Add Contribution</p>
                <Select value={newContrib.category} onValueChange={(v) => setNewContrib({ ...newContrib, category: v })}>
                  <SelectTrigger className="bg-white border-[#1a1a2e]/10 rounded-xl" data-testid="new-contrib-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CONTRIBUTION_CATEGORIES.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2"><c.icon className="w-4 h-4" />{c.name}<span className="text-xs text-[#1a1a2e]/40 ml-1">- {c.desc}</span></span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newContrib.category && (
                  <>
                    <Input value={newContrib.title} onChange={(e) => setNewContrib({ ...newContrib, title: e.target.value })}
                      className="bg-white border-[#1a1a2e]/10 rounded-xl" placeholder="Title (e.g. 'Served in Vietnam War')" data-testid="new-contrib-title" />
                    <Textarea value={newContrib.description} onChange={(e) => setNewContrib({ ...newContrib, description: e.target.value })}
                      className="bg-white border-[#1a1a2e]/10 rounded-xl" placeholder="Description..." rows={3} data-testid="new-contrib-description" />
                    <Input value={newContrib.year} onChange={(e) => setNewContrib({ ...newContrib, year: e.target.value })}
                      className="bg-white border-[#1a1a2e]/10 rounded-xl w-32" placeholder="Year" data-testid="new-contrib-year" />
                    <Button onClick={handleAddContribution} disabled={addingContrib || !newContrib.title.trim()}
                      className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full shadow-lg" data-testid="add-contrib-button">
                      {addingContrib ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Add Contribution
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map(img => (
                <div key={img.id} className="relative aspect-square rounded-2xl overflow-hidden group shadow-lg">
                  <img src={img.image_url.startsWith('http') ? img.image_url : `${img.image_url}`} alt={img.caption || ''} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#1a1a2e]/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteGalleryImage(img.id)}
                      className="text-white hover:bg-red-500/50 rounded-full" data-testid={`delete-gallery-${img.id}`}>
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                  {img.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1a1a2e]/80 to-transparent p-3">
                      <p className="text-xs text-white">{img.caption}</p>
                    </div>
                  )}
                </div>
              ))}
              <div className="aspect-square rounded-2xl border-2 border-dashed border-[#1a1a2e]/10 flex items-center justify-center cursor-pointer hover:border-[#7c3aed]/30 transition-colors"
                onClick={() => galleryInputRef.current?.click()} data-testid="gallery-upload-area">
                {uploadingGallery ? <Loader2 className="w-8 h-8 text-[#7c3aed] animate-spin" /> : (
                  <div className="text-center"><Image className="w-8 h-8 text-[#1a1a2e]/20 mx-auto mb-2" /><p className="text-xs text-[#1a1a2e]/40">Add images</p></div>
                )}
              </div>
            </div>
            <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleGalleryUpload} />
          </TabsContent>

          {/* Extols Tab */}
          <TabsContent value="extols" className="space-y-6">
            <Card className="border-[#1a1a2e]/5 bg-white shadow-lg">
              <CardContent className="pt-6">
                <Label className="text-[#1a1a2e]/70 mb-2 block">Add a word of appreciation</Label>
                <Textarea value={newExtol} onChange={(e) => setNewExtol(e.target.value)}
                  className="bg-[#FDFBF7] border-[#1a1a2e]/5 rounded-xl mb-4" rows={2}
                  placeholder={`"${editedProfile.person_name} taught me..." or "I'm grateful for..."`} data-testid="new-extol-input" />
                <Button onClick={handleAddExtol} disabled={addingExtol || !newExtol.trim()}
                  className="bg-gradient-to-r from-[#db2777] to-[#7c3aed] text-white rounded-full shadow-lg" data-testid="add-extol-button">
                  {addingExtol ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}Add Extol
                </Button>
              </CardContent>
            </Card>
            {extols.length === 0 ? (
              <p className="text-center text-[#1a1a2e]/40 py-8">No extols yet.</p>
            ) : extols.map(extol => (
              <Card key={extol.id} className="border-[#1a1a2e]/5 bg-white shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div><p className="text-[#1a1a2e]/80 mb-2">"{extol.message}"</p><p className="text-sm text-[#1a1a2e]/40">- {extol.author_name}</p></div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteExtol(extol.id)}
                      className="text-[#1a1a2e]/30 hover:text-red-500" data-testid={`delete-extol-${extol.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-white border-[#1a1a2e]/10">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a2e]">Invite Contributors</DialogTitle>
            <DialogDescription className="text-[#1a1a2e]/50">Share links with people you want to contribute.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {invites.map(invite => (
              <div key={invite.id} className="flex items-center gap-2 p-3 bg-[#FDFBF7] rounded-xl border border-[#1a1a2e]/5">
                <LinkIcon className="w-4 h-4 text-[#1a1a2e]/40 shrink-0" />
                <span className="flex-1 text-sm text-[#1a1a2e]/70 truncate">...?invite={invite.token.slice(0, 8)}...</span>
                <Button variant="ghost" size="sm" onClick={() => copyInviteLink(invite.token)}
                  className="text-[#7c3aed]" data-testid={`copy-invite-${invite.id}`}>
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            ))}
            <Button onClick={handleCreateInvite}
              className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full shadow-lg" data-testid="create-invite-button">
              <Plus className="w-4 h-4 mr-2" />Create new invite link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Photo Gallery Dialog */}
      <Dialog open={showStockPhotos} onOpenChange={setShowStockPhotos}>
        <DialogContent className="bg-white border-[#1a1a2e]/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a2e]">Choose a Cover Photo</DialogTitle>
            <DialogDescription className="text-[#1a1a2e]/50">Select a landscape or flower photo for your tribute.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 max-h-[400px] overflow-y-auto">
            {STOCK_PHOTOS.map((photo) => (
              <button key={photo.label} onClick={() => handleSelectStockPhoto(photo.url)}
                className="aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-[#7c3aed] transition-all hover:scale-105 focus:outline-none focus:border-[#7c3aed]"
                data-testid={`stock-photo-${photo.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <img src={photo.url} alt={photo.label} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <p className="text-xs text-[#1a1a2e]/30 mt-2 text-center">Or upload your own photo by clicking the photo area</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
